import React, { useState, useEffect } from 'react';
import { groupsAPI, customersAPI, usersAPI } from '../services/api';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [groupsData, customersData] = await Promise.all([
        groupsAPI.getGroups(),
        customersAPI.getCustomers(),
      ]);
      setGroups(groupsData);
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setFormData({ name: '', description: '', customer_id: '', is_active: true });
    setShowModal(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      customer_id: group.customer_id,
      is_active: group.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    try {
      await groupsAPI.deleteGroup(groupId);
      alert('Group deleted successfully');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleViewMembers = async (group) => {
    setSelectedGroup(group);
    try {
      const [membersData, usersData] = await Promise.all([
        groupsAPI.getMembers(group._id),
        usersAPI.getUsers(),
      ]);
      setMembers(membersData);
      setAllUsers(usersData);
      setShowMembersModal(true);
    } catch (error) {
      alert('Failed to load members');
    }
  };

  const handleAddMember = async (userId) => {
    if (!selectedGroup) return;
    try {
      await groupsAPI.addMember(selectedGroup._id, userId);
      const membersData = await groupsAPI.getMembers(selectedGroup._id);
      setMembers(membersData);
      alert('Member added');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedGroup || !window.confirm('Remove this member?')) return;
    try {
      await groupsAPI.removeMember(selectedGroup._id, userId);
      setMembers(members.filter(m => m.id !== userId));
    } catch (error) {
      alert('Failed to remove member');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await groupsAPI.updateGroup(editingGroup._id, formData);
        alert('Group updated successfully');
      } else {
        await groupsAPI.createGroup(formData);
        alert('Group created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save group');
    }
  };

  const getCustomerName = (customerId) => {
    const c = customers.find(c => c._id === customerId);
    return c ? c.name : 'Unknown';
  };

  const nonMemberUsers = allUsers.filter(u => !members.find(m => m.id === u._id));

  if (loading) {
    return <div style={styles.container}><p>Loading...</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>Group Management</h1>
        <button style={styles.createBtn} onClick={handleCreate}>Create Group</button>
      </div>

      <div style={styles.tableSection}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group._id} style={styles.tableRow}>
                <td style={styles.td}>{group.name}</td>
                <td style={styles.td}>{getCustomerName(group.customer_id)}</td>
                <td style={styles.td}>{group.description || 'N/A'}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, backgroundColor: group.is_active ? '#27ae60' : '#e74c3c' }}>
                    {group.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  <button style={styles.actionBtn} onClick={() => handleViewMembers(group)}>Members</button>
                  <button style={styles.actionBtn} onClick={() => handleEdit(group)}>Edit</button>
                  <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => handleDelete(group._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>{editingGroup ? 'Edit Group' : 'Create Group'}</h2>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer</label>
                <select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} style={styles.input} required>
                  <option value="">Select Customer</option>
                  {customers.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ ...styles.input, minHeight: '80px' }} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} style={styles.checkbox} />
                  Active
                </label>
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.submitBtn}>{editingGroup ? 'Update' : 'Create'}</button>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMembersModal && selectedGroup && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: '600px' }}>
            <h2>Members of {selectedGroup.name}</h2>
            {members.length === 0 ? <p>No members in this group.</p> : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>Username</th>
                    <th style={styles.th}>Full Name</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} style={styles.tableRow}>
                      <td style={styles.td}>{m.username}</td>
                      <td style={styles.td}>{m.full_name}</td>
                      <td style={styles.td}>{m.email}</td>
                      <td style={styles.td}>
                        <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => handleRemoveMember(m.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {nonMemberUsers.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <label style={styles.label}>Add Member:</label>
                <select
                  style={styles.input}
                  value=""
                  onChange={(e) => { if (e.target.value) handleAddMember(e.target.value); }}
                >
                  <option value="">Select user...</option>
                  {nonMemberUsers.map((u) => (
                    <option key={u._id} value={u._id}>{u.username} - {u.full_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ marginTop: '1rem' }}>
              <button style={styles.cancelBtn} onClick={() => setShowMembersModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  pageTitle: { color: '#60a5fa', margin: 0, fontWeight: '700', textShadow: '0 0 10px rgba(96, 165, 250, 0.3)' },
  createBtn: { padding: '0.75rem 1.5rem', backgroundColor: '#60a5fa', backgroundImage: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(96, 165, 250, 0.3)' },
  tableSection: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  tableHeader: { backgroundColor: '#111827', backgroundImage: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)', borderBottom: '2px solid #60a5fa' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: '600', letterSpacing: '0.3px', color: '#e2e8f0' },
  tableRow: { borderBottom: '1px solid #374151' },
  td: { padding: '1rem', color: '#9ca3af' },
  badge: { padding: '0.25rem 0.75rem', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: '600' },
  actionBtn: { padding: '0.5rem 1rem', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem', fontSize: '14px' },
  deleteBtn: { backgroundColor: '#e74c3c' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '1rem' },
  modalContent: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #374151', color: '#e2e8f0' },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column' },
  label: { marginBottom: '0.5rem', color: '#9ca3af', fontWeight: '500', fontSize: '14px' },
  input: { padding: '0.75rem', border: '1px solid #374151', borderRadius: '8px', fontSize: '16px', backgroundColor: '#111827', color: '#e2e8f0', outline: 'none' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0', fontWeight: '500' },
  checkbox: { width: '20px', height: '20px' },
  modalActions: { display: 'flex', gap: '1rem', marginTop: '1rem' },
  submitBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#27ae60', backgroundImage: 'linear-gradient(135deg, #27ae60 0%, #219a52 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#374151', color: '#e2e8f0', border: '1px solid #4b5563', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
};

export default Groups;
